import { useState } from "react";
import { Facebook, Instagram, Menu, X, Youtube } from "lucide-react";
import { LogoMark, scrollToId } from "./decor";
import { NAV_LINKS, SOCIALS } from "../data/siteData";
import { useLang } from "../i18n";

const SOCIAL_ICONS = { facebook: Facebook, instagram: Instagram, youtube: Youtube };

const FlagDE = () => (
  <svg width="24" height="16" viewBox="0 0 24 16" aria-hidden="true">
    <rect width="24" height="5.4" fill="#000000" />
    <rect y="5.4" width="24" height="5.3" fill="#DD0000" />
    <rect y="10.7" width="24" height="5.3" fill="#FFCE00" />
  </svg>
);

const FlagUS = () => (
  <svg width="24" height="16" viewBox="0 0 24 16" aria-hidden="true">
    <rect width="24" height="16" fill="#B22234" />
    {[1, 3, 5, 7, 9, 11].map((i) => (
      <rect key={i} y={(i * 16) / 13} width="24" height={16 / 13} fill="#FFFFFF" />
    ))}
    <rect width="10.5" height="8.6" fill="#3C3B6E" />
  </svg>
);

const MobileLangFlags = () => {
  const { lang, setLang } = useLang();
  return (
    <div className="flex items-center gap-2 lg:hidden" data-testid="lang-flags-mobile">
      <button
        data-testid="lang-flag-de"
        onClick={() => setLang("de")}
        aria-label="Deutsch"
        className={`p-1.5 border transition-colors duration-300 ${lang === "de" ? "border-red-600 shadow-[0_0_10px_rgba(220,38,38,0.35)]" : "border-zinc-800 opacity-70"}`}
      >
        <FlagDE />
      </button>
      <button
        data-testid="lang-flag-en"
        onClick={() => setLang("en")}
        aria-label="English"
        className={`p-1.5 border transition-colors duration-300 ${lang === "en" ? "border-red-600 shadow-[0_0_10px_rgba(220,38,38,0.35)]" : "border-zinc-800 opacity-70"}`}
      >
        <FlagUS />
      </button>
    </div>
  );
};

const LangToggle = ({ mobile = false }) => {
  const { lang, setLang } = useLang();
  return (
    <div className={`flex items-center border border-zinc-800 ${mobile ? "" : "ml-2"}`} data-testid={mobile ? "lang-toggle-mobile" : "lang-toggle"}>
      {["de", "en"].map((l) => (
        <button
          key={l}
          data-testid={`lang-switch-${l}${mobile ? "-mobile" : ""}`}
          onClick={() => setLang(l)}
          className={`font-mono-tech text-[11px] tracking-[0.2em] uppercase px-3 py-1.5 transition-colors duration-300 ${
            lang === l ? "bg-red-600 text-white" : "text-zinc-500 hover:text-white"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
};

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const { t } = useLang();

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <header
      data-testid="main-navbar"
      className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-black/80 border-b border-zinc-800"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between">
        <button
          data-testid="nav-logo-button"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center gap-3 group"
        >
          <LogoMark size={26} className="transition-transform duration-500 group-hover:rotate-90 shrink-0" />
          <img
            src="/crayfox-wordmark.png"
            alt="CRAYFOX"
            data-testid="nav-brand-logo"
            className="h-7 w-auto"
          />
        </button>

        <nav className="hidden lg:flex items-center gap-6">
          {NAV_LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l.id)}
              className="font-mono-tech text-xs tracking-[0.25em] uppercase text-zinc-400 hover:text-white transition-colors duration-300"
            >
              {t.nav[l.id]}
            </button>
          ))}
          <div className="flex items-center gap-1 border-l border-zinc-800 pl-4">
            {SOCIALS.map((s) => {
              const Icon = SOCIAL_ICONS[s.id];
              return (
                <a
                  key={s.id}
                  data-testid={`nav-social-${s.id}`}
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`CRAYFOX on ${s.label}`}
                  className="p-2 text-zinc-400 hover:text-red-500 transition-colors duration-300"
                >
                  {Icon ? <Icon size={17} /> : <span className="font-display text-base tracking-wider leading-none">{s.text}</span>}
                </a>
              );
            })}
          </div>
          <LangToggle />
          <button
            data-testid="nav-book-now-button"
            onClick={() => go("booking")}
            className="font-mono-tech text-xs tracking-[0.25em] uppercase bg-red-600 hover:bg-red-700 text-white px-5 py-2.5 transition-colors duration-300"
          >
            {t.nav.book}
          </button>
        </nav>

        <div className="flex items-center gap-3 lg:hidden">
          <MobileLangFlags />
          <button
            data-testid="nav-mobile-menu-button"
            onClick={() => setOpen(!open)}
            className="text-zinc-300 hover:text-white"
            aria-label="Toggle menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="nav-mobile-menu" className="lg:hidden border-t border-zinc-800 bg-black/95">
          <div className="px-6 py-4 flex flex-col gap-4">
            {NAV_LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-mobile-link-${l.id}`}
                onClick={() => go(l.id)}
                className="text-left font-mono-tech text-sm tracking-[0.25em] uppercase text-zinc-300 hover:text-white"
              >
                {t.nav[l.id]}
              </button>
            ))}
            <button
              data-testid="nav-mobile-book-now-button"
              onClick={() => go("booking")}
              className="font-mono-tech text-sm tracking-[0.25em] uppercase bg-red-600 hover:bg-red-700 text-white px-5 py-3 text-center"
            >
              {t.nav.book}
            </button>
            <div className="flex items-center justify-between pt-2 border-t border-zinc-800">
              <div className="flex gap-4">
                {SOCIALS.map((s) => {
                  const Icon = SOCIAL_ICONS[s.id];
                  return (
                    <a
                      key={s.id}
                      data-testid={`nav-mobile-social-${s.id}`}
                      href={s.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`CRAYFOX on ${s.label}`}
                      className="p-2 text-zinc-400 hover:text-red-500 transition-colors duration-300"
                    >
                      {Icon ? <Icon size={20} /> : <span className="font-display text-lg tracking-wider leading-none">{s.text}</span>}
                    </a>
                  );
                })}
              </div>
              <LangToggle mobile />
            </div>
          </div>
        </div>
      )}
    </header>
  );
};
